import { Column, Entity, Index, JoinColumn, ManyToOne } from 'typeorm';
import { BaseEntity } from './base.entity';
import type { UserEntity } from './user.entity';

/** `tenant` = shared office maintained by Admin / Sales Manager; `user` = personal (home, hotel). */
export type NamedLocationScope = 'tenant' | 'user';

/**
 * A labelled place a sales trip can start or end at (PD-15919, decision 8 in
 * docs/claude/sales-routes/00-handoff.md). Warehouses are NOT named locations —
 * the trip start/end picker offers them as a second source — and tenant office
 * locations are not warehouses.
 *
 * Coordinates are required (the address comes from a Google Places selection
 * or a geocoded manual entry) because the point of a start location is the
 * drive-time chain, which needs a fix.
 */
@Entity('named_location')
@Index('ix_named_location_scope_user', ['scope', 'userId'])
export class NamedLocationEntity extends BaseEntity {
	@Column({ name: 'name', type: 'varchar', length: 128 })
	public name!: string;

	@Column({ name: 'scope', type: 'varchar', length: 10 })
	public scope!: NamedLocationScope;

	/** Owner for `scope = 'user'` (workd user id); null for tenant locations. */
	@Column({ name: 'fk_user__id', type: 'int', unsigned: true, nullable: true })
	public userId?: number | null;

	@Column({ name: 'address1', type: 'varchar', length: 255, nullable: true })
	public address1?: string | null;

	@Column({ name: 'address2', type: 'varchar', length: 255, nullable: true })
	public address2?: string | null;

	@Column({ name: 'city', type: 'varchar', length: 128, nullable: true })
	public city?: string | null;

	@Column({ name: 'state', type: 'varchar', length: 64, nullable: true })
	public state?: string | null;

	@Column({ name: 'postalCode', type: 'varchar', length: 32, nullable: true })
	public postalCode?: string | null;

	@Column({ name: 'country', type: 'varchar', length: 64, nullable: true })
	public country?: string | null;

	// Same precision as client_address so the two kinds of point round-trip
	// identically through the drive-time math.
	@Column({ name: 'latitude', type: 'decimal', precision: 10, scale: 8 })
	public latitude!: string;

	@Column({ name: 'longitude', type: 'decimal', precision: 11, scale: 8 })
	public longitude!: string;

	@Column({ name: 'placeId', type: 'varchar', length: 255, nullable: true })
	public placeId?: string | null;

	/** At most one per user for `scope = 'user'` — the rep's usual "Home". */
	@Column({ name: 'isDefaultStart', type: 'boolean', default: false })
	public isDefaultStart!: boolean;

	@Column({ name: 'isActive', type: 'boolean', default: true })
	public isActive!: boolean;

	@ManyToOne('UserEntity', {
		nullable: true,
		createForeignKeyConstraints: false,
	})
	@JoinColumn([{ name: 'fk_user__id', referencedColumnName: 'workdUserId' }])
	public user?: UserEntity | null;
}
